'use client';

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { Image } from '@/components/client';

import { ANIMATION_CONFIG, REVIEWS, TYPOGRAPHY_IMAGES } from '../constants';

export const Recommend = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const carouselInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % REVIEWS.length);
    }, ANIMATION_CONFIG.carouselRotationIntervalMs);

    return () => clearInterval(carouselInterval);
  }, []);

  const currentReview = REVIEWS[currentIndex];
  const nextReview = REVIEWS[(currentIndex + 1) % REVIEWS.length];
  const thirdReview = REVIEWS[(currentIndex + 2) % REVIEWS.length];

  return (
    <section
      id="recommend"
      // eslint-disable-next-line tailwindcss/no-contradicting-classname
      className="to-white flex w-full flex-col items-center gap-32 bg-gradient-to-b from-[#FF6F00] from-[53.87%] px-40 pb-60 pt-80"
      aria-labelledby="review-heading"
    >
      <div className="flex flex-col items-center gap-6 text-center">
        <p className="font-700 text-Gray-white typo-h5">
          웰로 기부자의 찐 후기
        </p>
        <h2 id="review-heading">
          <Image
            src={TYPOGRAPHY_IMAGES.recommendHeading.url}
            alt={TYPOGRAPHY_IMAGES.recommendHeading.alt}
            width={TYPOGRAPHY_IMAGES.recommendHeading.width}
            height={TYPOGRAPHY_IMAGES.recommendHeading.height}
            loading="lazy"
            className="object-contain"
          />
        </h2>
      </div>

      <div
        className="relative h-240 w-full overflow-hidden"
        role="region"
        aria-label="기부자 후기 캐러셀"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="sr-only" role="status" aria-live="polite">
          {currentReview.text}, {currentReview.author}
        </div>
        <motion.div
          key={`carousel-${currentIndex}`}
          initial={{ y: 0 }}
          animate={{ y: -132 }}
          transition={{
            duration: ANIMATION_CONFIG.transitionDurationSeconds,
            ease: ANIMATION_CONFIG.easingCurve,
          }}
          className="flex w-full flex-col items-start gap-20 px-20"
          style={{
            WebkitMask:
              'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
            mask: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
          }}
        >
          <ReviewCard
            text={currentReview.text}
            author={currentReview.author}
            opacity={1}
            animateOpacity={0}
            isActive={true}
          />
          <ReviewCard
            text={nextReview.text}
            author={nextReview.author}
            opacity={0.3}
            animateOpacity={1}
            isActive={false}
          />
          <ReviewCard
            text={thirdReview.text}
            author={thirdReview.author}
            opacity={0.3}
            animateOpacity={0.3}
            isActive={false}
          />
        </motion.div>
      </div>
    </section>
  );
};

type ReviewCardProps = {
  text: string;
  author: string;
  opacity?: number;
  animateOpacity?: number;
  isActive?: boolean;
};

const ReviewCard = ({
  text,
  author,
  opacity = 1,
  animateOpacity = 1,
  isActive = false,
}: ReviewCardProps) => {
  return (
    <motion.article
      initial={{ opacity }}
      animate={{ opacity: animateOpacity }}
      transition={{
        duration: ANIMATION_CONFIG.transitionDurationSeconds,
        ease: 'easeOut',
      }}
      className="flex w-full flex-col items-center gap-10 rounded-b-[60px] rounded-tl-[60px] bg-Gray-white px-20 py-16"
      aria-hidden={!isActive}
    >
      <SvgStars />
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="break-all font-700 text-Gray-900 typo-body2">
          &quot;{text}&quot;
        </p>
        <p className="font-400 text-Gray-500 typo-body4">{author}</p>
      </div>
    </motion.article>
  );
};

const SvgStars = () => {
  return (
    <svg
      width="100"
      height="20"
      viewBox="0 0 100 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="별점 5점 만점"
      role="img"
    >
      <path
        d="M10 0L12.25 6.91H19.51L13.63 11.18L15.88 18.09L10 13.82L4.12 18.09L6.37 11.18L0.49 6.91H7.75L10 0Z"
        fill="#FFD233"
      />
      <path
        d="M30 0L32.25 6.91H39.51L33.63 11.18L35.88 18.09L30 13.82L24.12 18.09L26.37 11.18L20.49 6.91H27.75L30 0Z"
        fill="#FFD233"
      />
      <path
        d="M50 0L52.25 6.91H59.51L53.63 11.18L55.88 18.09L50 13.82L44.12 18.09L46.37 11.18L40.49 6.91H47.75L50 0Z"
        fill="#FFD233"
      />
      <path
        d="M70 0L72.25 6.91H79.51L73.63 11.18L75.88 18.09L70 13.82L64.12 18.09L66.37 11.18L60.49 6.91H67.75L70 0Z"
        fill="#FFD233"
      />
      <path
        d="M90 0L92.25 6.91H99.51L93.63 11.18L95.88 18.09L90 13.82L84.12 18.09L86.37 11.18L80.49 6.91H87.75L90 0Z"
        fill="#FFD233"
      />
    </svg>
  );
};
